import type { AxiosError } from 'axios';
import { ApiError, type ApiEnvelope, type Page } from '@/types/api';

/** Symbol-keyed stash for pagination metadata on list responses. */
export const PAGE_SYMBOL = Symbol('page');

export interface WithPage {
  [PAGE_SYMBOL]?: Page;
}

/**
 * Unwrap the backend `{ status, code, message, data, page }` envelope.
 * Returns `data` on success (stashing `page` on a symbol for list hooks),
 * throws a typed {@link ApiError} otherwise.
 */
export function unwrap<T>(body: ApiEnvelope<T>): T {
  if (body && body.status === true) {
    const data = body.data;
    if (data && typeof data === 'object' && body.page) {
      try {
        (data as WithPage)[PAGE_SYMBOL] = body.page;
      } catch {
        /* frozen/primitive — ignore */
      }
    }
    return data;
  }
  throw new ApiError({
    message: body?.message || 'Request failed',
    code: body?.code ?? 400,
    payload: body,
  });
}

/** Read pagination metadata stashed by {@link unwrap}. */
export function getPage(data: unknown): Page | undefined {
  if (data && typeof data === 'object') {
    return (data as WithPage)[PAGE_SYMBOL];
  }
  return undefined;
}

interface BackendErrorBody {
  code?: number;
  message?: string;
  errorCode?: string;
  blocked?: boolean;
  data?: { code?: number; message?: string; errorCode?: string };
}

/** Convert an axios error into a typed {@link ApiError}, preserving errorCode. */
export function toApiError(error: unknown): ApiError {
  const axiosErr = error as AxiosError<BackendErrorBody>;

  if (axiosErr?.code === 'ERR_NETWORK' || axiosErr?.message === 'Network Error') {
    return new ApiError({
      message: 'Tidak dapat terhubung ke server.',
      code: 0,
      isNetwork: true,
    });
  }

  const status = axiosErr?.response?.status ?? 500;
  const body = axiosErr?.response?.data;
  const nested = body?.data ?? body;

  return new ApiError({
    message: nested?.message || body?.message || axiosErr?.message || 'Terjadi kesalahan.',
    code: nested?.code ?? body?.code ?? status,
    errorCode: nested?.errorCode ?? body?.errorCode ?? null,
    payload: body,
  });
}
